import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Tooltip from "../../common/Tooltip/Tooltip";
import WatermarkColorMenu from "../../../features/tools/watermark/Popup/WatermarkColorMenu";
import {
  OVERLAY_COLOR,
  OVERLAY_POSITION,
  useWatermarkToolbarOverlay,
} from "../../../features/tools/watermark/Popup/WatermarkToolbarOverlayContext";
import {
  DEFAULT_TEXT_FORMAT,
  TEXT_ALIGN_OPTIONS,
  TEXT_CASE_OPTIONS,
  TEXT_EFFECT_OPTIONS,
  TEXT_FORMAT_TOOLS,
  TEXT_SCRIPT_OPTIONS,
  TEXT_VERTICAL_ALIGN_OPTIONS,
  isAlignOptionEnabled,
  resolveSizeMenuOptions,
} from "./textFormatModel";
import TextFormatFloatingMenu from "./TextFormatFloatingMenu";
import "./TextFormatToolbar.css";

const TftTipContext = createContext(null);

const ChevronIcon = () => (
  <svg className="tft-icon tft-icon--sm" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlignLeftIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M2 3h10M2 6.5h8M2 10h10M2 13.5h6" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);
const AlignCenterIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3 3h10M4 6.5h8M3 10h10M5 13.5h6" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);
const AlignRightIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4 3h10M6 6.5h8M4 10h10M8 13.5h6" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const AlignJustifyIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M2 3h12M2 6.5h12M2 10h12M2 13.5h12" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const SpacingIcon = () => (
  <svg className="tft-icon tft-icon--spacing" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M5.2 3.2h5.6M8 3.2v7.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M3 12.6h10M3 12.6l1.6-1.5M3 12.6l1.6 1.5M13 12.6l-1.6-1.5M13 12.6l-1.6 1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const AnchorTopIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4 3.5h8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 6v6.5M8 12.5l-2-2M8 12.5l2-2" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const AnchorMiddleIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M5 3.5h6M5 12.5h6" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
    <path d="M8 5.2v5.6M8 5.2l-1.7 1.7M8 5.2l1.7 1.7M8 10.8l-1.7-1.7M8 10.8l1.7-1.7" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const AnchorBottomIcon = () => (
  <svg className="tft-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4 12.5h8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 3.5v6.5M8 3.5l-2 2M8 3.5l2 2" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const alignIconFor = (id) => {
  if (id === "left") return <AlignLeftIcon />;
  if (id === "right") return <AlignRightIcon />;
  if (id === "justify") return <AlignJustifyIcon />;
  return <AlignCenterIcon />;
};

/**
 * Hover tip pinned 2px under the toolbar bottom edge (not under each button).
 */
const TftTip = ({ label, children }) => {
  const toolbarRef = useContext(TftTipContext);
  return label ? (
    <Tooltip
      content={label}
      position="bottom"
      size="menu"
      gap={2}
      edgeRef={toolbarRef}
      flip={false}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};

/** Tip for disabled controls inside menus (near the control, not toolbar edge). */
const TftDisabledTip = ({ label, children }) =>
  label ? (
    <Tooltip content={label} position="top" size="wide" gap={6} flip block>
      {children}
    </Tooltip>
  ) : (
    children
  );

/**
 * Universal text formatting toolbar (Canva-style).
 * Controlled component — no store imports. Host supplies value/onChange.
 */
const TextFormatToolbar = ({
  value = DEFAULT_TEXT_FORMAT,
  onChange = () => {},
  fonts = [],
  tools = [],
  disabled = false,
  sizeConfig = {
    min: 0,
    max: 24,
    step: 0.1,
    formatDisplay: (v) => String(Math.round(v * 10)),
  },
  /** Host align rules — omit/empty = all options enabled (reusable toolbar). */
  alignConfig = {},
  /** Host spacing-panel rules — omit = show all sections (reusable toolbar). */
  spacingConfig = {},
  colorPickerAnchorSelector = ".text-format-toolbar",
  className = "",
}) => {
  const [fontOpen, setFontOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const [spacingOpen, setSpacingOpen] = useState(false);
  const [alignOpen, setAlignOpen] = useState(false);
  const [effectOpen, setEffectOpen] = useState(false);
  const [opacityOpen, setOpacityOpen] = useState(false);
  const fontBtnRef = useRef(null);
  const sizeControlRef = useRef(null);
  const sizeInputRef = useRef(null);
  const skipSizeCommitRef = useRef(false);
  const caseBtnRef = useRef(null);
  const spacingBtnRef = useRef(null);
  const alignGroupRef = useRef(null);
  const effectBtnRef = useRef(null);
  const opacityBtnRef = useRef(null);
  const toolbarRef = useRef(null);

  const overlayCtx = useWatermarkToolbarOverlay();
  const useLeftPanelOverlay = Boolean(overlayCtx);

  const toolSet = useMemo(() => new Set(tools), [tools]);
  const has = (id) => toolSet.has(id);

  const font =
    fonts.find((f) => f.id === value.fontId) || fonts[0] || { id: "Arial", label: "Arial", stack: "Arial, sans-serif" };

  const patch = (partial) => onChange({ ...value, ...partial });

  const closeMenus = (...except) => {
    if (!except.includes("font")) setFontOpen(false);
    if (!except.includes("size")) setSizeOpen(false);
    if (!except.includes("color")) {
      setColorOpen(false);
      if (useLeftPanelOverlay && overlayCtx?.isColorOpen) overlayCtx.closeOverlay();
    }
    if (!except.includes("case")) setCaseOpen(false);
    if (!except.includes("spacing")) setSpacingOpen(false);
    if (!except.includes("align")) setAlignOpen(false);
    if (!except.includes("effect")) setEffectOpen(false);
    if (!except.includes("opacity")) setOpacityOpen(false);
  };

  const colorActive = useLeftPanelOverlay ? overlayCtx.isColorOpen : colorOpen;
  const positionActive = useLeftPanelOverlay
    ? overlayCtx.isPositionOpen
    : false;

  useEffect(() => {
    if (!overlayCtx?.overlay) return;
    setFontOpen(false);
    setSizeOpen(false);
    setCaseOpen(false);
    setSpacingOpen(false);
    setAlignOpen(false);
    setEffectOpen(false);
    setOpacityOpen(false);
    setColorOpen(false);
  }, [overlayCtx?.overlay]);

  const sizeDisplay = sizeConfig.formatDisplay
    ? sizeConfig.formatDisplay(value.size)
    : String(value.size);
  const sizeMenuOptions = useMemo(
    () => resolveSizeMenuOptions(sizeConfig),
    [sizeConfig]
  );
  const sizeMin = sizeConfig.min ?? 0;
  const sizeMax = sizeConfig.max ?? 24;
  const parseSizeDisplay =
    sizeConfig.parseDisplay ??
    ((display) => {
      const n = Number(display);
      return Number.isFinite(n) ? n : sizeMin;
    });
  const [sizeDraft, setSizeDraft] = useState(sizeDisplay);
  const [sizeInputFocused, setSizeInputFocused] = useState(false);

  useEffect(() => {
    if (!sizeInputFocused) setSizeDraft(sizeDisplay);
  }, [sizeDisplay, sizeInputFocused]);

  const clampInternalSize = (internal) =>
    Math.max(sizeMin, Math.min(sizeMax, internal));

  const applyDisplaySize = (displayValue) => {
    patch({ size: clampInternalSize(parseSizeDisplay(displayValue)) });
  };

  const stepDisplaySize = (delta) => {
    const idx = sizeMenuOptions.findIndex((o) => o.display === sizeDisplay);
    if (idx >= 0) {
      const next = sizeMenuOptions[idx + delta];
      if (next) {
        patch({ size: next.internal });
        return;
      }
    }
    const current = Number(sizeDisplay);
    const base = Number.isFinite(current) ? current : Number(sizeMenuOptions[0]?.display ?? 0);
    applyDisplaySize(base + delta);
  };

  const commitSizeDraft = (raw) => {
    const cleaned = String(raw ?? "").trim();
    if (!cleaned) {
      setSizeDraft(sizeDisplay);
      return;
    }
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      setSizeDraft(sizeDisplay);
      return;
    }
    applyDisplaySize(n);
  };

  const openSizeEditor = () => {
    if (disabled) return;
    closeMenus("size");
    setSizeOpen(true);
    window.requestAnimationFrame(() => {
      const input = sizeInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    });
  };

  const alignDefaultId = alignConfig.defaultId || "center";
  const rawAlignId = value.textAlign || alignDefaultId;
  const resolvedAlignId = isAlignOptionEnabled(rawAlignId, alignConfig)
    ? rawAlignId
    : alignDefaultId;
  const alignLabel =
    TEXT_ALIGN_OPTIONS.find((a) => a.id === resolvedAlignId)?.label ||
    "Alignment";

  const toolbarClassName = [
    "text-format-toolbar",
    disabled ? "is-disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TftTipContext.Provider value={toolbarRef}>
      {!useLeftPanelOverlay ? (
        <WatermarkColorMenu
          isOpen={colorOpen}
          onClose={() => setColorOpen(false)}
          color={value.color}
          onChange={(c) => patch({ color: c })}
          onApply={(c) => patch({ color: c })}
          anchorSelector={colorPickerAnchorSelector}
          anchorGap={12}
          ignoreCloseSelectors={[
            ".text-format-toolbar",
            ".tft-color-btn",
            ".wmc-color-menu",
          ]}
        />
      ) : null}
      <div
        ref={toolbarRef}
        className={toolbarClassName}
        role="toolbar"
        aria-label="Text formatting"
      >
        <div className="text-format-toolbar__scroll">
        {has(TEXT_FORMAT_TOOLS.FONT) ? (
          <div className="tft-group tft-font">
            <TftTip label="Font">
              <button
                ref={fontBtnRef}
                type="button"
                className={`tft-font-btn${fontOpen ? " is-open" : ""}`}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={fontOpen}
                onClick={() => {
                  closeMenus("font");
                  setFontOpen((v) => !v);
                }}
              >
                <span className="tft-font-btn__preview" style={{ fontFamily: font.stack }}>
                  Ag
                </span>
                <span className="tft-font-btn__label">{font.label}</span>
                <ChevronIcon />
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={fontOpen}
              anchorRef={fontBtnRef}
              onClose={() => setFontOpen(false)}
              className="tft-menu tft-menu--font tft-menu--floating"
            >
              <ul role="listbox" aria-label="Font">
                {fonts.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={f.id === font.id}
                      className={`tft-menu__item${f.id === font.id ? " is-active" : ""}`}
                      style={{ fontFamily: f.stack }}
                      onClick={() => {
                        patch({ fontId: f.id });
                        setFontOpen(false);
                      }}
                    >
                      {f.label}
                    </button>
                  </li>
                ))}
              </ul>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.SIZE) ? (
          <div
            ref={sizeControlRef}
            className={`tft-size-control${sizeOpen ? " is-open" : ""}`}
            role="group"
            aria-label="Font size"
          >
            <TftTip label="Decrease font size">
              <button
                type="button"
                className="tft-size-step"
                disabled={disabled}
                aria-label="Decrease size"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  stepDisplaySize(-1);
                }}
              >
                −
              </button>
            </TftTip>
            <TftTip label="Font size">
              <div
                className="tft-size-field"
                onMouseDown={(e) => {
                  if (e.target.closest(".tft-size-input")) return;
                  e.preventDefault();
                  openSizeEditor();
                }}
              >
                <input
                ref={sizeInputRef}
                type="text"
                inputMode="numeric"
                className="tft-size-input"
                value={sizeDraft}
                disabled={disabled}
                aria-label="Font size value"
                aria-expanded={sizeOpen}
                aria-haspopup="listbox"
                onFocus={() => {
                  setSizeInputFocused(true);
                  if (!sizeOpen) setSizeOpen(true);
                }}
                onChange={(e) => setSizeDraft(e.target.value)}
                onBlur={(e) => {
                  setSizeInputFocused(false);
                  if (skipSizeCommitRef.current) {
                    skipSizeCommitRef.current = false;
                    return;
                  }
                  commitSizeDraft(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                    setSizeOpen(false);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setSizeDraft(sizeDisplay);
                    e.currentTarget.blur();
                    setSizeOpen(false);
                  }
                }}
              />
              </div>
            </TftTip>
            <TftTip label="Increase font size">
              <button
                type="button"
                className="tft-size-step"
                disabled={disabled}
                aria-label="Increase size"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  stepDisplaySize(1);
                }}
              >
                +
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={sizeOpen}
              anchorRef={sizeControlRef}
              onClose={() => setSizeOpen(false)}
              className="tft-menu tft-menu--size tft-menu--floating"
              align="center"
            >
              <ul role="listbox" aria-label="Font size">
                {sizeMenuOptions.map(({ display, internal }) => (
                  <li key={display}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={display === sizeDisplay}
                      className={`tft-menu__item tft-menu__item--size${
                        display === sizeDisplay ? " is-active" : ""
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        // Prefer the option's internal size; skip blur commit so the
                        // still-focused input doesn't re-apply the previous draft.
                        skipSizeCommitRef.current = true;
                        patch({ size: internal });
                        setSizeDraft(display);
                        setSizeOpen(false);
                        sizeInputRef.current?.blur();
                      }}
                    >
                      {display}
                    </button>
                  </li>
                ))}
              </ul>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.COLOR) ? (
          <TftTip label="Text color">
            <button
              type="button"
              className={`tft-color-btn${colorActive ? " is-open" : ""}`}
              disabled={disabled}
              aria-label="Text color"
              aria-expanded={colorActive}
              onClick={() => {
                closeMenus("color");
                if (useLeftPanelOverlay) {
                  overlayCtx.openOverlay(OVERLAY_COLOR);
                } else {
                  setColorOpen((v) => !v);
                }
              }}
            >
              <span className="tft-color-btn__letter">A</span>
              <span className="tft-color-btn__bar tft-color-btn__bar--rainbow" aria-hidden="true" />
            </button>
          </TftTip>
        ) : null}

        <div className="tft-divider" aria-hidden="true" />

        {has(TEXT_FORMAT_TOOLS.BOLD) ? (
          <TftTip label="Bold">
            <button
              type="button"
              className={`tft-toggle-btn${value.fontWeight === "bold" ? " is-active" : ""}`}
              disabled={disabled}
              aria-pressed={value.fontWeight === "bold"}
              aria-label="Bold"
              onClick={() =>
                patch({ fontWeight: value.fontWeight === "bold" ? "normal" : "bold" })
              }
            >
              <span className="tft-glyph tft-glyph--bold">B</span>
            </button>
          </TftTip>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.ITALIC) ? (
          <TftTip label="Italic">
            <button
              type="button"
              className={`tft-toggle-btn${value.fontStyle === "italic" ? " is-active" : ""}`}
              disabled={disabled}
              aria-pressed={value.fontStyle === "italic"}
              aria-label="Italic"
              onClick={() =>
                patch({ fontStyle: value.fontStyle === "italic" ? "normal" : "italic" })
              }
            >
              <span className="tft-glyph tft-glyph--italic" aria-hidden="true">
                I
              </span>
            </button>
          </TftTip>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.UNDERLINE) ? (
          <TftTip label="Underline">
            <button
              type="button"
              className={`tft-toggle-btn${value.underline ? " is-active" : ""}`}
              disabled={disabled}
              aria-pressed={value.underline}
              aria-label="Underline"
              onClick={() => patch({ underline: !value.underline })}
            >
              <span className="tft-glyph tft-glyph--underline">U</span>
            </button>
          </TftTip>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.STRIKE) ? (
          <TftTip label="Strikethrough">
            <button
              type="button"
              className={`tft-toggle-btn${value.strikethrough ? " is-active" : ""}`}
              disabled={disabled}
              aria-pressed={value.strikethrough}
              aria-label="Strikethrough"
              onClick={() => patch({ strikethrough: !value.strikethrough })}
            >
              <span className="tft-glyph tft-glyph--strike">S</span>
            </button>
          </TftTip>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.CASE) ? (
          <div className="tft-group">
            <TftTip label="Text case">
              <button
                ref={caseBtnRef}
                type="button"
                className={`tft-toggle-btn tft-case-btn${caseOpen ? " is-open" : ""}`}
                disabled={disabled}
                aria-label="Text case"
                aria-expanded={caseOpen}
                onClick={() => {
                  closeMenus("case");
                  setCaseOpen((v) => !v);
                }}
              >
                <span className="tft-glyph tft-glyph--case">aA</span>
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={caseOpen}
              anchorRef={caseBtnRef}
              onClose={() => setCaseOpen(false)}
              className="tft-menu tft-menu--case tft-menu--floating"
            >
              <ul role="menu" aria-label="Text case">
                {TEXT_CASE_OPTIONS.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      role="menuitem"
                      className="tft-menu__item"
                      onClick={() => {
                        onChange({ ...value, textCase: c.id });
                        setCaseOpen(false);
                      }}
                    >
                      <span className="tft-menu__case-short">{c.short}</span>
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.SPACING) ? (
          <div className="tft-group tft-group--spacing">
            <TftTip label={spacingOpen ? null : "Spacing"}>
              <button
                ref={spacingBtnRef}
                type="button"
                className={`tft-toggle-btn${spacingOpen ? " is-open" : ""}`}
                disabled={disabled}
                aria-label="Advanced text settings"
                aria-expanded={spacingOpen}
                aria-haspopup="dialog"
                onClick={() => {
                  closeMenus("spacing");
                  setSpacingOpen((v) => !v);
                }}
              >
                <SpacingIcon />
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={spacingOpen}
              anchorRef={spacingBtnRef}
              onClose={() => setSpacingOpen(false)}
              align="center"
              ignoreCloseSelectors={[".tft-group--spacing", ".tft-menu--spacing"]}
              className="tft-menu tft-menu--spacing tft-menu--floating"
            >
              <div className="tft-advanced" role="dialog" aria-label="Spacing">
                <div className="tft-advanced__header">
                  <span className="tft-advanced__title">Spacing</span>
                </div>

                {(spacingConfig.showLetterSpacing !== false) ? (
                  <div className="tft-advanced__section">
                    <div className="tft-slider-field">
                      <div className="tft-slider-field__head">
                        <span className="tft-slider-field__label">Letter spacing</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="tft-slider-field__input"
                          value={Number(value.letterSpacing) || 0}
                          disabled={disabled}
                          aria-label="Letter spacing value"
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (Number.isFinite(n)) patch({ letterSpacing: n });
                          }}
                        />
                      </div>
                      <input
                        type="range"
                        className="tft-slider"
                        min={-100}
                        max={200}
                        step={1}
                        value={Number(value.letterSpacing) || 0}
                        disabled={disabled}
                        aria-label="Letter spacing"
                        onChange={(e) => patch({ letterSpacing: Number(e.target.value) })}
                      />
                    </div>

                    {spacingConfig.showLineHeight !== false ? (
                      <TftDisabledTip
                        label={
                          spacingConfig.lineHeightDisabled
                            ? spacingConfig.lineHeightDisabledTip ||
                              "Not available for this tool"
                            : null
                        }
                      >
                      <div
                        className={`tft-slider-field${
                          spacingConfig.lineHeightDisabled ? " is-disabled" : ""
                        }`}
                      >
                        <div className="tft-slider-field__head">
                          <span className="tft-slider-field__label">Line spacing</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="tft-slider-field__input"
                            value={Number(value.lineHeight) || 1.4}
                            disabled={disabled || Boolean(spacingConfig.lineHeightDisabled)}
                            aria-label="Line spacing value"
                            onChange={(e) => {
                              if (spacingConfig.lineHeightDisabled) return;
                              const n = Number(e.target.value);
                              if (Number.isFinite(n)) patch({ lineHeight: n });
                            }}
                          />
                        </div>
                        <input
                          type="range"
                          className="tft-slider"
                          min={0.5}
                          max={3}
                          step={0.1}
                          value={Number(value.lineHeight) || 1.4}
                          disabled={disabled || Boolean(spacingConfig.lineHeightDisabled)}
                          aria-label="Line spacing"
                          onChange={(e) => {
                            if (spacingConfig.lineHeightDisabled) return;
                            patch({ lineHeight: Number(e.target.value) });
                          }}
                        />
                      </div>
                      </TftDisabledTip>
                    ) : null}
                  </div>
                ) : null}

                {spacingConfig.showTextPosition !== false ? (
                  <div className="tft-advanced__section">
                    <div className="tft-advanced__section-label">Text position</div>
                    <div
                      className="tft-segment tft-segment--full"
                      role="group"
                      aria-label="Text position"
                    >
                      {TEXT_SCRIPT_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`tft-segment__btn tft-segment__btn--script${
                            (value.textScript || "normal") === opt.id ? " is-active" : ""
                          }`}
                          aria-pressed={(value.textScript || "normal") === opt.id}
                          aria-label={opt.label}
                          disabled={disabled}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => patch({ textScript: opt.id })}
                        >
                          <span className="tft-segment__script" aria-hidden="true">
                            <span className="tft-segment__script-base">{opt.base}</span>
                            <span
                              className={`tft-segment__script-exp tft-segment__script-exp--${opt.expPos}`}
                            >
                              {opt.exp}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {spacingConfig.showAnchor !== false ? (
                  <TftDisabledTip
                    label={
                      spacingConfig.anchorDisabled
                        ? spacingConfig.anchorDisabledTip ||
                          "Not available for this tool"
                        : null
                    }
                  >
                  <div
                    className={`tft-advanced__section${
                      spacingConfig.anchorDisabled ? " is-disabled" : ""
                    }`}
                  >
                    <div className="tft-advanced__section-label">Anchor text box</div>
                    <div
                      className="tft-segment tft-segment--full tft-segment--anchor"
                      role="group"
                      aria-label="Vertical anchor"
                    >
                      {TEXT_VERTICAL_ALIGN_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`tft-segment__btn${
                            (value.textVerticalAlign || "top") === opt.id ? " is-active" : ""
                          }`}
                          aria-pressed={(value.textVerticalAlign || "top") === opt.id}
                          aria-label={opt.label}
                          disabled={disabled || Boolean(spacingConfig.anchorDisabled)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (spacingConfig.anchorDisabled) return;
                            patch({ textVerticalAlign: opt.id });
                          }}
                        >
                          {opt.id === "top" ? (
                            <AnchorTopIcon />
                          ) : opt.id === "bottom" ? (
                            <AnchorBottomIcon />
                          ) : (
                            <AnchorMiddleIcon />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  </TftDisabledTip>
                ) : null}
              </div>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.ALIGN) ? (
          <div className="tft-group tft-group--align" ref={alignGroupRef}>
            <TftTip label={alignOpen ? null : alignLabel}>
              <button
                type="button"
                className={`tft-toggle-btn${alignOpen ? " is-open" : ""}${
                  resolvedAlignId !== alignDefaultId ? " is-active" : ""
                }`}
                disabled={disabled}
                aria-label="Text alignment"
                aria-expanded={alignOpen}
                aria-haspopup="menu"
                onClick={() => {
                  closeMenus("align");
                  setAlignOpen((v) => !v);
                }}
              >
                {alignIconFor(resolvedAlignId)}
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={alignOpen}
              anchorRef={alignGroupRef}
              onClose={() => setAlignOpen(false)}
              align="center"
              ignoreCloseSelectors={[".tft-group--align", ".tft-menu--align"]}
              className="tft-menu tft-menu--align tft-menu--floating"
            >
              <ul role="menu" aria-label="Alignment">
                {TEXT_ALIGN_OPTIONS.map((a) => {
                  const optionEnabled = isAlignOptionEnabled(a.id, alignConfig);
                  const tip =
                    optionEnabled
                      ? null
                      : alignConfig.disabledTip ||
                        `${a.label} isn’t available for this tool`;
                  const item = (
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={resolvedAlignId === a.id}
                      aria-disabled={!optionEnabled}
                      disabled={disabled || !optionEnabled}
                      className={`tft-menu__item${resolvedAlignId === a.id ? " is-active" : ""}${
                        !optionEnabled ? " is-disabled" : ""
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (!optionEnabled) return;
                        patch({ textAlign: a.id });
                        setAlignOpen(false);
                      }}
                    >
                      <span className="tft-menu__align-icon">{alignIconFor(a.id)}</span>
                      {a.label}
                    </button>
                  );
                  return (
                    <li key={a.id}>
                      {tip ? (
                        <TftDisabledTip label={tip}>
                          <span className="tft-menu__disabled-wrap">{item}</span>
                        </TftDisabledTip>
                      ) : (
                        item
                      )}
                    </li>
                  );
                })}
              </ul>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.EFFECT) ? (
          <div className="tft-group">
            <TftTip label="Effect">
              <button
                ref={effectBtnRef}
                type="button"
                className={`tft-toggle-btn tft-position-btn${
                  effectOpen ? " is-open" : ""
                }${
                  value.effect && value.effect !== "none" ? " is-active" : ""
                }`}
                disabled={disabled}
                aria-label="Effect"
                aria-expanded={effectOpen}
                aria-haspopup="menu"
                onClick={() => {
                  closeMenus("effect");
                  setEffectOpen((v) => !v);
                }}
              >
                Effect
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={effectOpen}
              anchorRef={effectBtnRef}
              onClose={() => setEffectOpen(false)}
              className="tft-menu tft-menu--flip tft-menu--floating"
              style={{ minWidth: 140, width: "max-content", maxWidth: "none" }}
            >
              <ul role="menu" aria-label="Effect">
                {TEXT_EFFECT_OPTIONS.map((opt) => {
                  const active = value.effect === opt.id;
                  return (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={active}
                        className={`tft-menu__item${active ? " is-active" : ""}`}
                        onClick={() => {
                          patch({ effect: active ? "none" : opt.id });
                          setEffectOpen(false);
                        }}
                      >
                        <span className="tft-menu__flip-label">{opt.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.OPACITY) ? (
          <div className="tft-group tft-opacity">
            <TftTip label="Opacity">
              <button
                ref={opacityBtnRef}
                type="button"
                className={`tft-toggle-btn tft-opacity-btn${opacityOpen ? " is-open" : ""}`}
                disabled={disabled}
                aria-label="Transparency"
                aria-expanded={opacityOpen}
                onClick={() => {
                  closeMenus("opacity");
                  setOpacityOpen((v) => !v);
                }}
              >
                <span className="tft-opacity-icon" aria-hidden="true" />
              </button>
            </TftTip>
            <TextFormatFloatingMenu
              open={opacityOpen}
              anchorRef={opacityBtnRef}
              onClose={() => setOpacityOpen(false)}
              align="center"
              className="tft-popover tft-popover--opacity tft-menu--floating"
            >
              <div className="tft-slider-field">
                <div className="tft-slider-field__head">
                  <span className="tft-slider-field__label">Opacity</span>
                  <div className="tft-slider-field__value-wrap">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="tft-slider-field__input"
                      value={Math.round(Number(value.opacity) || 0)}
                      disabled={disabled}
                      aria-label="Opacity value"
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n)) {
                          patch({ opacity: Math.max(0, Math.min(100, n)) });
                        }
                      }}
                    />
                    <span className="tft-slider-field__suffix">%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="tft-slider"
                  min={0}
                  max={100}
                  step={1}
                  value={Number(value.opacity) || 0}
                  disabled={disabled}
                  aria-label="Opacity"
                  onChange={(e) => patch({ opacity: Number(e.target.value) })}
                />
              </div>
            </TextFormatFloatingMenu>
          </div>
        ) : null}

        {has(TEXT_FORMAT_TOOLS.POSITION) && useLeftPanelOverlay ? (
          <TftTip label="Position">
            <button
              type="button"
              className={`tft-toggle-btn tft-position-btn${positionActive ? " is-open" : ""}`}
              disabled={disabled}
              aria-label="Position"
              aria-expanded={positionActive}
              onClick={() => {
                closeMenus("position");
                if (!overlayCtx.isPositionOpen) {
                  overlayCtx.openOverlay(OVERLAY_POSITION);
                }
              }}
            >
              Position
            </button>
          </TftTip>
        ) : null}
        </div>
      </div>
    </TftTipContext.Provider>
  );
};

export default TextFormatToolbar;
